import { Link } from "@tanstack/react-router";
import { SoundPlayer } from "@/components/sound-player";
import { useI18n, type Lang } from "@/lib/i18n";

/** A discreet sound bar offered on top of the lab page. */
export type LabSound = {
  src: string;
  cover: string;
  title: string;
  artist: string;
};

const backLabel: Record<Lang, string> = {
  pt: "← Voltar ao projeto",
  en: "← Back to the project",
  es: "← Volver al proyecto",
};

const defaultProjectSlug = "what-is-madness";

const defaultProjectTitle: Record<Lang, string> = {
  pt: "O que é a loucura?",
  en: "What Is Madness?",
  es: "¿Qué es la locura?",
};

const defaultProjectSuffix = "| O que é a loucura?";

export function LabPage({
  src,
  title,
  projectSlug = defaultProjectSlug,
  projectTitle = defaultProjectTitle,
  sound,
}: {
  src: string;
  title: string | Record<Lang, string>;
  projectSlug?: string;
  projectTitle?: Record<Lang, string>;
  sound?: LabSound;
}) {
  const { lang } = useI18n();
  const labTitle = typeof title === "string" ? title : title[lang];
  const iframeUrl = new URL(src);
  if (typeof title !== "string") iframeUrl.searchParams.set("lang", lang);

  return (
    <main id="main-content" className="fixed inset-0 flex h-dvh w-screen flex-col overflow-hidden bg-[#0b0b0b]">
      <header className="flex h-11 shrink-0 items-center justify-between gap-4 border-b border-bone/12 bg-[#0b0b0b] px-3 sm:h-10 sm:px-5">
        <Link
          to="/work/$slug"
          params={{ slug: projectSlug }}
          aria-label={`${backLabel[lang]} · ${projectTitle[lang]}`}
          className="-ml-3 inline-flex min-h-11 min-w-11 items-center px-3 font-mono text-[10px] uppercase tracking-[0.22em] text-bone transition-colors hover:text-bone/70 sm:-ml-5 sm:min-h-10"
        >
          {backLabel[lang]}
        </Link>
        <h1 className="truncate font-mono text-[9px] font-normal uppercase tracking-[0.28em] text-bone/70">
          {labTitle}
        </h1>
      </header>
      <iframe
        src={iframeUrl.href}
        title={labTitle}
        allow="fullscreen; autoplay; xr-spatial-tracking"
        className="block min-h-0 w-full flex-1 border-0 bg-ink"
      />
      {sound && (
        <div className="absolute bottom-4 right-4 z-10 w-[min(380px,80vw)]">
          <SoundPlayer src={sound.src} cover={sound.cover} title={sound.title} artist={sound.artist} />
        </div>
      )}
    </main>
  );
}

export function labHead(
  path: string,
  name: string,
  description: string,
  projectSuffix: string = defaultProjectSuffix,
) {
  const project = projectSuffix.replace(/^\s*\|\s*/, "").trim();
  const parts = [name];
  if (project && project !== name) parts.push(project);
  parts.push("Luiza Jesus do Prado");
  const title = parts.join(" | ");
  const url = `https://luizaprado.lovable.app${path}`;
  return {
    meta: [
      { title },
      { name: "description", content: description },
      { property: "og:title", content: title },
      { property: "og:description", content: description },
      { property: "og:type", content: "website" },
      { property: "og:url", content: url },
      { name: "twitter:card", content: "summary" },
    ],
  };
}
